'use client';
import { useEffect, useState, useMemo } from 'react';
import { useReadContract, useWriteContract, useWaitForTransactionReceipt, useAccount } from 'wagmi';
import { formatEther } from 'viem';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/sheet';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/card';
import { Badge } from '@/components/badge';
import { Button } from '@/components/button';
import { Link } from 'react-router-dom';
import { RiLoader4Line, RiImageLine, RiCoinsLine, RiCheckLine } from '@remixicon/react';
import { NFT_DAO_UTILS_CONTRACT_CONFIG, type NftDaoUtilsContractConfig } from '@/configs/nft.config';
import { LARISSA_DAO_CONTRACT_CONFIG } from '@/configs/larissa-dao.config';
import type { NftData, NftRewardData } from '@/abis/nft-dao-utils';
import { generateRewardKeys, formatRewardKey } from '@/utils/nft-rewards';

interface NftDetailSidebarProps {
	isOpen: boolean;
	nft: NftData | null;
	onClose: () => void;
	/** Defaults to V1 utils deployment */
	utilsConfig?: NftDaoUtilsContractConfig;
	/**
	 * When true, reward amounts stay visible but claiming is disabled (e.g. NFT V1 — claims go through V2 after migration).
	 */
	disableRewardClaim?: boolean;
}

export default function NftDetailSidebar({ isOpen, nft, onClose, utilsConfig, disableRewardClaim }: NftDetailSidebarProps) {
	const daoUtils = utilsConfig ?? NFT_DAO_UTILS_CONTRACT_CONFIG;
	const { address: userAccount } = useAccount();
	const [nftImage, setNftImage] = useState<string | null>(null);
	const [metadata, setMetadata] = useState<any>(null);
	const [claimingKey, setClaimingKey] = useState<string | null>(null);
	const [txHash, setTxHash] = useState<`0x${string}` | undefined>(undefined);

	// Generate reward keys from mint time to current month (latest first)
	const rewardKeys = useMemo(() => {
		if (!nft) return [];
		const TEMP_REWARD_HISTORY_START = BigInt(Math.floor(new Date(2024, 5, 1).getTime() / 1000));
		const keys = generateRewardKeys(import.meta.env.VITE_CHAIN_ID === '98098' ? TEMP_REWARD_HISTORY_START : nft.mintTime);
		return keys.reverse(); // Latest first
	}, [nft]);

	// Create a component for each reward period to use hooks properly
	const RewardPeriodItem = ({ periodKey }: { periodKey: string }) => {
		const { data: rewardData, isLoading } = useReadContract({
			address: daoUtils.address,
			abi: daoUtils.abi,
			functionName: 'getNftRewardData',
			args: nft ? [nft.tokenId, periodKey] : undefined,
			query: {
				enabled: isOpen && !!nft && !!daoUtils.address,
			},
		}) as { data: NftRewardData | undefined; isLoading: boolean };

		const { data: isClaimed } = useReadContract({
			address: LARISSA_DAO_CONTRACT_CONFIG.address,
			abi: LARISSA_DAO_CONTRACT_CONFIG.abi,
			functionName: 'nftRewardClaims',
			args: nft ? [periodKey, nft.tokenId] : undefined,
			query: {
				enabled: isOpen && !!nft && !!LARISSA_DAO_CONTRACT_CONFIG.address && !!rewardData?.isEligible,
			},
		}) as { data: boolean | undefined };

		const canClaim =
			!disableRewardClaim && rewardData?.isEligible && rewardData?.isFinalized && !(isClaimed ?? false);
		const showMigrateToClaimNote =
			disableRewardClaim && rewardData?.isEligible && rewardData?.isFinalized && !(isClaimed ?? false);
		const isClaiming = claimingKey === periodKey && (isWritePending || isTxPending);

		return (
			<div className="border border-border rounded-lg p-4 bg-background">
				<div className="flex items-center justify-between mb-3">
					<h4 className="font-semibold text-[#FF8A3C]">{formatRewardKey(periodKey)}</h4>
					{(isClaimed ?? false) && (
						<Badge variant="default" className="bg-green-500/20 text-green-400 border-green-500">
							<RiCheckLine className="size-3 mr-1" />
							Claimed
						</Badge>
					)}
				</div>

				{isLoading ? (
					<div className="flex items-center justify-center py-4">
						<RiLoader4Line className="size-5 animate-spin text-[#FF8A3C]" />
					</div>
				) : rewardData ? (
					<div className="space-y-3">
						{rewardData.isEligible ? (
							<>
								<div className="flex justify-between items-center">
									<span className="text-sm text-muted-foreground">LRS Reward</span>
									<span className="font-medium text-[#FF8A3C]">
										{formatEther(rewardData.rewardAmount)} LRS
									</span>
								</div>
								<div className="flex justify-between items-center">
									<span className="text-sm text-muted-foreground">LRSN Reward</span>
									<span className="font-medium text-[#FF8A3C]">
										{formatEther(rewardData.rewardAmountLRSN)} LRSN
									</span>
								</div>
								<div className="flex flex-col gap-2 pt-2 border-t border-border sm:flex-row sm:items-center sm:justify-between">
									<span className="text-xs text-muted-foreground">
										{rewardData.isFinalized ? 'Finalized' : 'Pending Finalization'}
									</span>
									{showMigrateToClaimNote && (
										<p className="text-xs text-muted-foreground text-right sm:max-w-[280px]">
											To claim this reward,{' '}
											<Link to="/nfts/v2" className="font-medium text-[#FF8A3C] hover:underline">
												migrate to NFT V2
											</Link>
										</p>
									)}
									{canClaim && (
										<Button
											size="sm"
											onClick={() => handleClaimReward(periodKey)}
											disabled={isClaiming || !userAccount}
											className="bg-[#FF8A3C] hover:bg-[#FF8A3C]/90"
										>
											{isClaiming ? (
												<>
													<RiLoader4Line className="size-4 animate-spin mr-2" />
													Claiming...
												</>
											) : (
												'Claim Rewards'
											)}
										</Button>
									)}
								</div>
							</>
						) : (
							<div className="text-sm text-muted-foreground">
								Not eligible for this period
							</div>
						)}
					</div>
				) : (
					<div className="text-sm text-muted-foreground">No reward data available</div>
				)}
			</div>
		);
	};

	// Write contract for claiming rewards
	const { writeContractAsync, isPending: isWritePending } = useWriteContract();

	// Watch transaction status
	const { isLoading: isTxPending, isSuccess: isTxSuccess } = useWaitForTransactionReceipt({
		hash: txHash,
		query: {
			enabled: !!txHash,
		},
	});

	// Reset transaction hash on success
	useEffect(() => {
		if (isTxSuccess) {
			setTxHash(undefined);
			setClaimingKey(null);
		}
	}, [isTxSuccess]);

	// Fetch NFT metadata from tokenURI
	useEffect(() => {
		if (!nft?.tokenURI) {
			setNftImage(null);
			setMetadata(null);
			return;
		}

		const fetchMetadata = async () => {
			try {
				// Handle IPFS URLs
				let url = nft.tokenURI;
				if (url.startsWith('ipfs://')) {
					url = `https://ipfs.io/ipfs/${url.replace('ipfs://', '')}`;
				}

				// Check if URL is a JSON file
				const isJsonUrl = url.toLowerCase().endsWith('.json') || url.toLowerCase().includes('.json');

				const response = await fetch(url);
				if (!response.ok) {
					throw new Error(`HTTP error! status: ${response.status}`);
				}

				// Check if response is JSON
				const contentType = response.headers.get('content-type');
				let data: any;

				if (contentType && contentType.includes('application/json')) {
					data = await response.json();
				} else if (isJsonUrl) {
					// URL is JSON file, try to parse as JSON
					const text = await response.text();
					try {
						data = JSON.parse(text);
					} catch (parseError) {
						throw new Error('Response is not valid JSON');
					}
				} else {
					// Try to parse as JSON anyway
					const text = await response.text();
					try {
						data = JSON.parse(text);
					} catch (parseError) {
						throw new Error('Response is not valid JSON');
					}
				}

				setMetadata(data);

				// Extract image URL
				if (data && data.image) {
					let imageUrl = data.image;
					if (imageUrl.startsWith('ipfs://')) {
						imageUrl = `https://ipfs.io/ipfs/${imageUrl.replace('ipfs://', '')}`;
					}
					setNftImage(imageUrl);
				} else {
					setNftImage(null);
				}
			} catch (error) {
				console.error('Error fetching NFT metadata:', error);
				setNftImage(null);
				setMetadata(null);
			}
		};

		fetchMetadata();
	}, [nft?.tokenURI]);

	const formatDate = (timestamp: bigint) => {
		return new Date(Number(timestamp) * 1000).toLocaleString();
	};

	const formatAddress = (address: string) => {
		return `${address.slice(0, 6)}...${address.slice(-4)}`;
	};

	const handleClaimReward = async (key: string) => {
		if (!nft || !LARISSA_DAO_CONTRACT_CONFIG.address) return;

		try {
			setClaimingKey(key);
			const hash = await writeContractAsync({
				address: LARISSA_DAO_CONTRACT_CONFIG.address,
				abi: LARISSA_DAO_CONTRACT_CONFIG.abi,
				functionName: 'claimNftMemberReward',
				args: [nft.tokenId, key],
			});
			setTxHash(hash);
		} catch (error) {
			console.error('Error claiming reward:', error);
			setClaimingKey(null);
			alert(error instanceof Error ? error.message : 'Failed to claim reward');
		}
	};

	if (!nft) {
		return null;
	}

	return (
		<Sheet open={isOpen} onOpenChange={onClose}>
			<SheetContent side="right" className="sm:max-w-lg overflow-y-auto">
				<SheetHeader>
					<SheetTitle className="text-[#FF8A3C]">NFT Details</SheetTitle>
					<SheetDescription>
						View NFT metadata and reward information
					</SheetDescription>
				</SheetHeader>

				<div className="space-y-6 mt-6">
					{/* NFT Image */}
					<Card className="bg-sidebar m-0 p-0 border border-border">
						<CardContent className="p-0">
							{nftImage ? (
								<img
									src={nftImage}
									alt={`NFT #${nft.tokenId.toString()}`}
									className="w-full h-auto rounded-lg object-contain max-h-96"
									onError={() => setNftImage(null)}
								/>
							) : (
								<div className="w-full h-64 bg-muted rounded-lg flex items-center justify-center">
									<RiImageLine className="size-16 text-muted-foreground" />
								</div>
							)}
						</CardContent>
					</Card>

					{/* Basic NFT Info */}
					<Card className="bg-sidebar border border-border">
						<CardHeader>
							<CardTitle className="text-lg">Basic Information</CardTitle>
						</CardHeader>
						<CardContent className="space-y-3">
							<div className="flex justify-between items-center">
								<span className="text-sm text-muted-foreground">Token ID</span>
								<span className="font-medium">#{nft.tokenId.toString()}</span>
							</div>
							<div className="flex justify-between items-center">
								<span className="text-sm text-muted-foreground">Level</span>
								<Badge variant="outline" className="bg-[#FF8A3C]/10 text-[#FF8A3C] border-[#FF8A3C]">
									Level {nft.level.toString()}
								</Badge>
							</div>
							<div className="flex justify-between items-center">
								<span className="text-sm text-muted-foreground">Mint Time</span>
								<span className="font-medium text-sm">{formatDate(nft.mintTime)}</span>
							</div>
							<div className="flex justify-between items-center">
								<span className="text-sm text-muted-foreground">Owner</span>
								<span className="font-mono text-sm">{formatAddress(nft.owner)}</span>
							</div>
						</CardContent>
					</Card>

					{/* Metadata */}
					{metadata && (
						<Card className="bg-sidebar border border-border">
							<CardHeader>
								<CardTitle className="text-lg">Metadata</CardTitle>
							</CardHeader>
							<CardContent className="space-y-3">
								{metadata.name && (
									<div>
										<span className="text-sm text-muted-foreground">Name</span>
										<p className="font-medium mt-1">{metadata.name}</p>
									</div>
								)}
								{metadata.description && (
									<div>
										<span className="text-sm text-muted-foreground">Description</span>
										<p className="text-sm mt-1">{metadata.description}</p>
									</div>
								)}
								{metadata.attributes && Array.isArray(metadata.attributes) && metadata.attributes.length > 0 && (
									<div>
										<span className="text-sm text-muted-foreground mb-2 block">Attributes</span>
										<div className="grid grid-cols-2 gap-2">
											{metadata.attributes.map((attr: any, index: number) => (
												<div key={index} className="bg-background p-2 rounded text-xs">
													<div className="font-medium">{attr.trait_type || 'Trait'}</div>
													<div className="text-muted-foreground">{attr.value}</div>
												</div>
											))}
										</div>
									</div>
								)}
							</CardContent>
						</Card>
					)}

					{/* Reward Information - All Periods */}
					<Card className="bg-sidebar border border-border">
						<CardHeader>
							<CardTitle className="text-lg flex items-center gap-2">
								<RiCoinsLine className="size-5 text-[#FF8A3C]" />
								Reward History
							</CardTitle>
						</CardHeader>
						<CardContent>
							{disableRewardClaim && (
								<p className="text-sm text-muted-foreground rounded-md border border-[#FF8A3C]/25 bg-[#FF8A3C]/5 px-3 py-2.5 mb-4 leading-relaxed">
									Rewards on NFT V1 are shown for reference only. If you want to claim rewards, migrate this
									NFT to{' '}
									<Link to="/nfts/v2" className="font-medium text-[#FF8A3C] hover:underline">
										NFT V2
									</Link>{' '}
									and claim from there.
								</p>
							)}
							{rewardKeys.length === 0 ? (
								<div className="text-center py-4 text-muted-foreground text-sm">
									No reward periods available.
								</div>
							) : (
								<div className="space-y-4">
									{rewardKeys.map((periodKey) => (
										<RewardPeriodItem key={periodKey} periodKey={periodKey} />
									))}
								</div>
							)}
						</CardContent>
					</Card>
				</div>
			</SheetContent>
		</Sheet>
	);
}
